import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  formatFieldLabel,
  formatMetricValue,
  formatTemporalLabel,
} from "../utils/visualizationData";
import {
  DEFAULT_TEMPORAL_CHART_HEIGHT,
  MAX_LEGEND_HEIGHT,
  TOOLTIP_Z_INDEX,
  getCategoricalLayout,
  getCategoryAxisLayout,
  getChartMargins,
  getNumericAxisWidth,
  getRankingLayout,
  shouldUseScrollableLegend,
  truncateLabelToWidth,
} from "../utils/visualizationLayout";
import classes from "../GenericQueryResultVisualization.module.css";

export const formatCompactNumber = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return "";
  const absolute = Math.abs(number);
  if (absolute >= 1_000_000) return Number((number / 1_000_000).toFixed(1)) + "M";
  if (absolute >= 1_000) return Number((number / 1_000).toFixed(1)) + "K";
  return String(number);
};

const formatRankingValue = (value) =>
  typeof value === "number" && Number.isFinite(value)
    ? value.toLocaleString("en-US", { maximumFractionDigits: 20 })
    : String(value ?? "");

const formatRankingMetricValue = (value) =>
  value === null || value === undefined ? "Not available" : formatRankingValue(value);

const formatRankingTooltipValue = (field, value) =>
  /(?:^|_)(?:year|reporting_year|month|round|round_number)$/.test(String(field || "").toLowerCase())
    ? String(value ?? "")
    : formatRankingValue(value);
const getNumericValues = (model) =>
  model.data.flatMap((point) =>
    model.series
      .map(({ dataKey }) => Number(point?.[dataKey]))
      .filter(Number.isFinite),
  );

const CategoryAxisTick = ({
  x,
  y,
  payload,
  maximumWidth,
  orientation = "horizontal",
}) => {
  const { displayLabel, fullLabel, truncated } = truncateLabelToWidth({
    label: payload?.value,
    maximumWidth,
  });
  const horizontal = orientation === "horizontal";
  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={horizontal ? -5 : 0}
        y={horizontal ? 0 : 9}
        dy={horizontal ? 4 : 8}
        textAnchor={horizontal ? "end" : "middle"}
        fill="#475569"
        fontSize={11}
        aria-label={fullLabel}
        data-full-label={fullLabel}
        data-truncated={truncated ? "true" : "false"}
      >
        <title>{fullLabel}</title>
        {displayLabel}
      </text>
    </g>
  );
};

const ChartLegend = ({ series }) => {
  if (series.length <= 1) return null;
  const scrollable = shouldUseScrollableLegend(series.length);
  return (
    <div
      className={classes.legend_wrapper}
      data-testid="generic-visualization-legend"
      data-scrollable={scrollable ? "true" : "false"}
      style={scrollable ? { maxHeight: MAX_LEGEND_HEIGHT } : undefined}
      tabIndex={scrollable ? 0 : undefined}
      aria-label="Chart legend"
    >
      <ul className={classes.legend_list}>
        {series.map((item) => (
          <li key={item.identity} className={classes.legend_item}>
            <span
              className={classes.legend_marker}
              style={{ backgroundColor: item.color }}
              aria-hidden="true"
            />
            <span>{String(item.label || item.value)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export const ChartTooltip = ({
  active,
  payload,
  label,
  coordinate,
  mode,
  metricLabel,
  viewByLabel,
  selectedContext,
  hasBreakdown,
  chartRegionRef,
  ranking,
}) => {
  const tooltipRef = useRef(null);
  const [position, setPosition] = useState({ left: 12, top: 12 });
  const rawPayload = payload || [];
  const visiblePayload = ranking
    ? rawPayload.slice(0, 1)
    : rawPayload.filter(({ value }) => value !== null && value !== undefined);
  const payloadSignature = visiblePayload
    .map(({ dataKey, name, value }) => `${dataKey || name}:${value}`)
    .join("|");

  useLayoutEffect(() => {
    if (!active || !visiblePayload.length || !tooltipRef.current || !chartRegionRef.current) return;
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const chartRect = chartRegionRef.current.getBoundingClientRect();
    const anchorLeft = chartRect.left + Number(coordinate?.x || 0);
    const anchorTop = chartRect.top + Number(coordinate?.y || 0);
    const viewportPadding = 12;
    const preferredLeft = anchorLeft + 14;
    const preferredTop = anchorTop + 14;
    const left = Math.max(
      viewportPadding,
      Math.min(preferredLeft, window.innerWidth - tooltipRect.width - viewportPadding),
    );
    const top = preferredTop + tooltipRect.height <= window.innerHeight - viewportPadding
      ? preferredTop
      : Math.max(viewportPadding, anchorTop - tooltipRect.height - 14);
    setPosition({ left, top });
  }, [
    active,
    chartRegionRef,
    coordinate?.x,
    coordinate?.y,
    label,
    payload?.length,
    payloadSignature,
    visiblePayload.length,
  ]);

  if (!active || !visiblePayload.length || typeof document === "undefined") return null;
  const pointContext = visiblePayload[0]?.payload?.__context || [];
  const rankingPoint = visiblePayload[0]?.payload?.__ranking || null;

  return createPortal(
    <div
      ref={tooltipRef}
      className={classes.tooltip_portal}
      style={{ left: position.left, top: position.top }}
      data-testid="generic-visualization-tooltip"
    >
      <div className={classes.tooltip}>
        {ranking && rankingPoint ? (
          <>
            <div className={classes.tooltip_title}>{rankingPoint.entityLabel}</div>
            <div className={classes.tooltip_context}>Rank: {String(rankingPoint.displayRank)}</div>
            <div className={classes.tooltip_row}>
              <span
                className={classes.tooltip_marker}
                style={{ backgroundColor: visiblePayload[0]?.color }}
                aria-hidden="true"
              />
              <strong>
                {rankingPoint.metricLabel}: {formatRankingMetricValue(rankingPoint.metricValue)}
              </strong>
            </div>
            {rankingPoint.parent && (
              <div className={classes.tooltip_context}>
                {rankingPoint.parent.label}: {String(rankingPoint.parent.value)}
              </div>
            )}
            {rankingPoint.tooltipFields.map(({ field, label: fieldLabel, value }) => (
              <div key={field} className={classes.tooltip_context}>
                {fieldLabel}: {formatRankingTooltipValue(field, value)}
              </div>
            ))}
            {selectedContext.map(({ label: contextLabel, value }) => (
              <div key={contextLabel} className={classes.tooltip_context}>
                {contextLabel}: {String(value)}
              </div>
            ))}
          </>
        ) : (
          <>
            <div className={classes.tooltip_title}>
              {mode === "temporal"
                ? formatTemporalLabel(label)
                : viewByLabel + ": " + String(visiblePayload[0]?.payload?.label ?? label)}
            </div>
            {visiblePayload.map((entry) => (
              <div key={entry.dataKey || entry.name} className={classes.tooltip_row}>
                <span
                  className={classes.tooltip_marker}
                  style={{ backgroundColor: entry.color }}
                  aria-hidden="true"
                />
                <div>
                  {mode === "temporal" && hasBreakdown && (
                    <span>{viewByLabel}: {entry.name}</span>
                  )}
                  <strong>
                    {mode === "temporal" ? metricLabel : entry.name}: {formatMetricValue(entry.value)}
                  </strong>
                </div>
              </div>
            ))}
            {pointContext.map(({ field, value }) => (
              <div key={field} className={classes.tooltip_context}>
                {formatFieldLabel(field)}: {String(value)}
              </div>
            ))}
            {selectedContext.map(({ label: contextLabel, value }) => (
              <div key={contextLabel} className={classes.tooltip_context}>
                {contextLabel}: {String(value)}
              </div>
            ))}
          </>
        )}
      </div>
    </div>,
    document.body,
  );
};

const tooltipProps = {
  allowEscapeViewBox: { x: true, y: true },
  wrapperStyle: { zIndex: TOOLTIP_Z_INDEX, pointerEvents: "none" },
};

export default function AdaptiveQueryResultChart({
  metricLabel,
  mode,
  model,
  ranking = null,
  selectedContext,
  viewByLabel,
}) {
  const regionRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(720);

  useEffect(() => {
    const element = regionRef.current;
    if (!element) return undefined;
    const updateWidth = () => {
      const nextWidth = element.getBoundingClientRect().width || element.clientWidth;
      if (nextWidth > 0) setContainerWidth(Math.round(nextWidth));
    };
    updateWidth();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(updateWidth);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const tooltip = (
    <ChartTooltip
      metricLabel={metricLabel}
      mode={mode}
      selectedContext={selectedContext}
      viewByLabel={viewByLabel}
      hasBreakdown={model.hasBreakdown}
      chartRegionRef={regionRef}
      ranking={ranking}
    />
  );
  const numericValues = getNumericValues(model);
  const numericAxisWidth = getNumericAxisWidth({
    values: numericValues,
    formatter: formatCompactNumber,
  });
  const scrollableLegend = shouldUseScrollableLegend(model.series.length);
  const legendMode = model.series.length <= 1
    ? "none"
    : scrollableLegend ? "scrollable" : "recharts";

  if (mode === "temporal") {
    const margins = getChartMargins({ chartType: "temporal" });
    return (
      <div
        ref={regionRef}
        className={classes.chart_region}
        data-testid="generic-visualization-chart"
        data-chart-mode="temporal"
        data-chart-height={DEFAULT_TEMPORAL_CHART_HEIGHT}
        data-plot-height={DEFAULT_TEMPORAL_CHART_HEIGHT}
        data-numeric-axis-width={numericAxisWidth}
        data-margin-left={margins.left}
        data-margin-right={margins.right}
        data-tooltip-z-index={TOOLTIP_Z_INDEX}
        data-legend-mode={legendMode}
        data-series-count={model.series.length}
        role="img"
        aria-label={metricLabel + " over time" + (model.hasBreakdown ? " by " + viewByLabel : "")}
        tabIndex={0}
      >
        <div className={classes.chart_area}>
          <ResponsiveContainer width="100%" height={DEFAULT_TEMPORAL_CHART_HEIGHT}>
            <LineChart data={model.data} margin={margins}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5eaf3" />
              <XAxis
                dataKey="__time"
                tick={{ fontSize: 11 }}
                tickFormatter={formatTemporalLabel}
                minTickGap={24}
                padding={{ left: 8, right: 8 }}
              />
              <YAxis
                width={numericAxisWidth}
                tick={{ fontSize: 11 }}
                tickFormatter={formatCompactNumber}
                tickMargin={6}
              />
              <Tooltip content={tooltip} {...tooltipProps} />
              {model.series.map((series) => (
                <Line
                  key={series.identity}
                  type="linear"
                  dataKey={series.dataKey}
                  name={String(series.value)}
                  stroke={series.color}
                  strokeWidth={2.25}
                  dot={{ r: 2, fill: series.color, stroke: series.color }}
                  activeDot={{ r: 5, fill: series.color, stroke: series.color, strokeWidth: 2 }}
                  connectNulls={false}
                  isAnimationActive={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
        <ChartLegend series={model.series} />
      </div>
    );
  }

  const horizontal = model.orientation !== "vertical";
  const layout = ranking
    ? getRankingLayout(model.data.length)
    : getCategoricalLayout({
        categoryCount: model.data.length,
        metricCount: model.series.length,
        orientation: model.orientation,
      });
  const labels = model.data.map(({ label }) => label);
  const categoryAxis = getCategoryAxisLayout({ labels, containerWidth });
  const verticalCategoryWidth = Math.max(
    34,
    Math.min(100, (containerWidth - numericAxisWidth - 44) / Math.max(1, labels.length) - 8),
  );
  const margins = getChartMargins({ chartType: "categorical", orientation: horizontal ? "horizontal" : "vertical" });
  const metricDescription = model.series.map(({ label, value }) => label || value).join(", ");

  return (
    <div
      ref={regionRef}
      className={classes.chart_region}
      data-testid="generic-visualization-chart"
      data-chart-mode={ranking ? "ranking_horizontal_bar" : horizontal ? "horizontal_bar" : "vertical_bar"}
      data-ranking-mode={ranking?.mode || undefined}
      data-category-order={model.data.map(({ label }) => label).join("|")}
      data-series-color={model.series[0]?.color || undefined}
      data-legend-mode={legendMode}
      data-chart-height={layout.chartHeight}
      data-plot-height={layout.chartHeight}
      data-row-height={layout.rowHeight}
      data-bar-size={layout.barSize}
      data-bar-gap={layout.barGap}
      data-category-gap={layout.categoryGap}
      data-category-axis-width={horizontal ? categoryAxis.axisWidth : verticalCategoryWidth}
      data-numeric-axis-width={numericAxisWidth}
      data-margin-left={margins.left}
      data-margin-right={margins.right}
      data-tooltip-z-index={TOOLTIP_Z_INDEX}
      data-series-count={model.series.length}
      role="img"
      aria-label={ranking?.title || ((model.series.length > 1 ? metricDescription : metricLabel) + " by " + viewByLabel)}
      tabIndex={0}
    >
      <div className={classes.chart_area}>
        <ResponsiveContainer width="100%" height={layout.chartHeight}>
          <BarChart
            data={model.data}
            layout={horizontal ? "vertical" : "horizontal"}
            barGap={layout.barGap}
            barCategoryGap={layout.categoryGap}
            margin={margins}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e5eaf3" />
            {horizontal ? (
              <>
                <XAxis
                  type="number"
                  tick={{ fontSize: 11 }}
                  tickFormatter={formatCompactNumber}
                  padding={{ left: 0, right: 8 }}
                />
                <YAxis
                  type="category"
                  dataKey="label"
                  width={categoryAxis.axisWidth}
                  tickMargin={4}
                  tick={(props) => (
                    <CategoryAxisTick
                      {...props}
                      maximumWidth={categoryAxis.maximumWidth}
                      orientation="horizontal"
                    />
                  )}
                />
              </>
            ) : (
              <>
                <XAxis
                  type="category"
                  dataKey="label"
                  interval={0}
                  height={42}
                  tick={(props) => (
                    <CategoryAxisTick
                      {...props}
                      maximumWidth={verticalCategoryWidth}
                      orientation="vertical"
                    />
                  )}
                />
                <YAxis
                  type="number"
                  width={numericAxisWidth}
                  tick={{ fontSize: 11 }}
                  tickFormatter={formatCompactNumber}
                  tickMargin={6}
                />
              </>
            )}
            <Tooltip content={tooltip} {...tooltipProps} />
            {model.series.map((series) => (
              <Bar
                key={series.identity}
                dataKey={series.dataKey}
                name={series.label || String(series.value)}
                fill={series.color}
                activeBar={ranking ? { fill: series.color } : undefined}
                barSize={layout.barSize}
                radius={horizontal ? [0, 4, 4, 0] : [4, 4, 0, 0]}
                isAnimationActive={false}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
      <ChartLegend series={model.series} />
    </div>
  );
}